import type { ReactNode } from 'react';

/**
 * What a season adds to the stage, and where it goes.
 *
 * ## A registry, so a holiday is one entry
 *
 * `StageScene` renders `<Decor>` at four fixed points in its draw order and
 * asks nothing about which theme is up. Adding one is an entry in the table at
 * the bottom of this file, the recolours in `stage.css` under `.stage-<key>`,
 * and a line in `config/stage-themes.php` - and nothing in the scene itself is
 * touched, which matters because the scene is four hundred shapes of converted
 * path data that nobody wants to edit seven times a year.
 *
 * ## Nothing in the base scene is redrawn
 *
 * Every theme is decoration over it plus CSS recolours of what is already
 * there. Take the class off and remove the decor and the room is exactly the
 * room it was - which is what makes turning a season off on the first of
 * November a setting rather than a deploy.
 *
 * ## Position outside, animation inside
 *
 * Every moving piece is two groups: an outer one carrying `transform` and an
 * inner one carrying the animation class. A CSS `transform` *replaces* the
 * attribute rather than composing with it, so an animated element with its
 * position on the same node snaps to the origin the moment the keyframes take
 * over - which is exactly what the first Halloween drop did to its bats, its
 * spider and its ghost. The v1.5 art fixes it; this file inherits the fix.
 *
 * ## Machine-converted, like the rest of the venue
 *
 * From `dev/stage-v1.5` through `scripts/convert-character.mjs`, the same script
 * and the same ritual as the scene and the characters. The motion classes
 * (`fall`, `burst`, `pulse`, `bat`, `ghost`, `spider`, `fog`, `jack`) are the
 * drop's own and live in `stage.css` beside the rest of the venue's.
 *
 * ## Symbols travel with the layer that instances them
 *
 * A firework, a heart, a leaf and a shamrock are `<use>` instances, and the
 * `<defs>` for each sits inside the group that draws it rather than in the
 * scene's own defs. The scene is split across five SVG documents; a definition
 * in one is invisible to the others, and a decor layer that carried a reference
 * to something declared elsewhere would render as nothing at all.
 */

/**
 * Where a piece of decor sits in the scene's order.
 *
 * The names are the four places the drawing has room: behind the truss, in the
 * air between the beams and the screen, on the stage behind the crowd, and in
 * front of everything. A theme fills the ones it needs.
 */
export type DecorSlot = 'sky' | 'air' | 'stage' | 'front';

/**
 * A moon behind the truss.
 *
 * Two circles: the lit disc and a dark one offset over it, which is how you
 * carve a crescent without a mask.
 */
function HalloweenSky() {
  return (
      <g id="layer-hw-sky" stroke="none">
        <circle cx="1712" cy="238" r="54" fill="#F2E8C9" opacity="0.92" />
        <circle cx="1690" cy="222" r="46" fill="#171618" />
        <circle cx="1730" cy="252" r="6" fill="#D8CBA4" opacity="0.7" />
        <circle cx="1714" cy="272" r="4" fill="#D8CBA4" opacity="0.7" />
      </g>
  );
}

/**
 * Cobwebs in both truss corners, a spider on a thread, and bats over the
 * screen.
 *
 * The right web is the left one mirrored about the viewBox rather than a second
 * drawing, so the two can never drift apart. The bats reuse the crowd's stagger
 * classes so four copies of one flight loop are four bats rather than a
 * formation.
 */
function HalloweenAir() {
  return (
      <g id="layer-hw-air">
        <g stroke="none" fill="none" opacity="0.75">
          <path d="M138 58 L138 190 M138 58 L270 58 M138 58 L232 152" stroke="#4a4458" strokeWidth="2" />
          <path d="M138 108 Q160 104 173 93 Q184 76 188 58" stroke="#4a4458" strokeWidth="2" />
          <path d="M138 158 Q180 150 208 128 Q232 96 238 58" stroke="#4a4458" strokeWidth="2" />
        </g>
        <g transform="translate(1920,0) scale(-1,1)" stroke="none" fill="none" opacity="0.75">
          <path d="M138 58 L138 190 M138 58 L270 58 M138 58 L232 152" stroke="#4a4458" strokeWidth="2" />
          <path d="M138 108 Q160 104 173 93 Q184 76 188 58" stroke="#4a4458" strokeWidth="2" />
          <path d="M138 158 Q180 150 208 128 Q232 96 238 58" stroke="#4a4458" strokeWidth="2" />
        </g>
        <g transform="translate(560,58)">
          <g className="spider">
            <path d="M0 0 L0 128" fill="none" stroke="#6a6478" strokeWidth="2" />
            <circle cx="0" cy="136" r="11" fill="#1c1a20" stroke="#3a3045" strokeWidth="2" />
            <circle cx="0" cy="122" r="6" fill="#1c1a20" stroke="#3a3045" strokeWidth="2" />
            <path d="M-10 130 Q-24 124 -30 112 M-11 138 Q-26 138 -34 130 M10 130 Q24 124 30 112 M11 138 Q26 138 34 130" fill="none" stroke="#3a3045" strokeWidth="2.5" />
            <circle cx="-3.5" cy="134" r="1.8" fill="#FF7A1A" stroke="none" />
            <circle cx="3.5" cy="134" r="1.8" fill="#FF7A1A" stroke="none" />
          </g>
        </g>
        <g transform="translate(700,200)">
          <g className="bat d1" fill="#0B0B0B" stroke="#3a3045" strokeWidth="2">
            <path d="M0 6 C-8 -6 -20 -10 -34 -4 C-24 0 -20 6 -22 14 C-13 8 -6 10 0 18 C6 10 13 8 22 14 C20 6 24 0 34 -4 C20 -10 8 -6 0 6 Z" />
            <path d="M-6 -2 L-9 -12 L-1 -5 Z M6 -2 L9 -12 L1 -5 Z" />
          </g>
        </g>
        <g transform="translate(1010,300) scale(0.8)">
          <g className="bat d3" fill="#0B0B0B" stroke="#3a3045" strokeWidth="2">
            <path d="M0 6 C-8 -6 -20 -10 -34 -4 C-24 0 -20 6 -22 14 C-13 8 -6 10 0 18 C6 10 13 8 22 14 C20 6 24 0 34 -4 C20 -10 8 -6 0 6 Z" />
            <path d="M-6 -2 L-9 -12 L-1 -5 Z M6 -2 L9 -12 L1 -5 Z" />
          </g>
        </g>
        <g transform="translate(1260,170) scale(1.15)">
          <g className="bat d5" fill="#0B0B0B" stroke="#3a3045" strokeWidth="2">
            <path d="M0 6 C-8 -6 -20 -10 -34 -4 C-24 0 -20 6 -22 14 C-13 8 -6 10 0 18 C6 10 13 8 22 14 C20 6 24 0 34 -4 C20 -10 8 -6 0 6 Z" />
            <path d="M-6 -2 L-9 -12 L-1 -5 Z M6 -2 L9 -12 L1 -5 Z" />
          </g>
        </g>
        <g transform="translate(850,440) scale(0.7)">
          <g className="bat d6" fill="#0B0B0B" stroke="#3a3045" strokeWidth="2">
            <path d="M0 6 C-8 -6 -20 -10 -34 -4 C-24 0 -20 6 -22 14 C-13 8 -6 10 0 18 C6 10 13 8 22 14 C20 6 24 0 34 -4 C20 -10 8 -6 0 6 Z" />
            <path d="M-6 -2 L-9 -12 L-1 -5 Z M6 -2 L9 -12 L1 -5 Z" />
          </g>
        </g>
      </g>
  );
}

/**
 * A ghost by the speakers and two jack-o'-lanterns flanking the booth.
 *
 * Behind the crowd on purpose: the people in the room stand in front of the
 * pumpkins, which is where people stand.
 */
function HalloweenStage() {
  return (
      <g id="layer-hw-stage">
        <g transform="translate(1462,540)" stroke="none">
          <g className="ghost">
            <path d="M0 -40 C-22 -40 -26 -14 -26 6 L-26 26 Q-20 18 -13 26 Q-6 34 0 26 Q6 18 13 26 Q20 34 26 26 L26 6 C26 -14 22 -40 0 -40 Z" fill="#F2F2F2" opacity="0.85" />
            <ellipse cx="-8" cy="-16" rx="3.5" ry="5" fill="#241f26" />
            <ellipse cx="8" cy="-16" rx="3.5" ry="5" fill="#241f26" />
            <ellipse cx="0" cy="-4" rx="3" ry="4.5" fill="#241f26" opacity="0.8" />
          </g>
        </g>
        <g transform="translate(600,808)">
          <circle cx="0" cy="-34" r="56" fill="#FF7A1A" opacity="0.15" stroke="none" />
          <ellipse cx="-24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="0" cy="-34" rx="34" ry="36" fill="#FF7A1A" stroke="#241f26" strokeWidth="5" />
          <rect x="-6" y="-84" width="12" height="20" rx="5" fill="#4a6b53" stroke="#241f26" strokeWidth="4" transform="rotate(-8)" />
          <g className="jack" stroke="none" fill="#FFD23F">
            <path d="M-22 -44 L-8 -44 L-15 -56 Z" />
            <path d="M22 -44 L8 -44 L15 -56 Z" />
            <path d="M-20 -26 L-12 -20 L-6 -26 L0 -20 L6 -26 L12 -20 L20 -26 L14 -12 L-14 -12 Z" />
          </g>
        </g>
        <g transform="translate(1330,808) scale(0.78)">
          <circle cx="0" cy="-34" r="56" fill="#FF7A1A" opacity="0.15" stroke="none" />
          <ellipse cx="-24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="0" cy="-34" rx="34" ry="36" fill="#FF7A1A" stroke="#241f26" strokeWidth="5" />
          <rect x="-6" y="-84" width="12" height="20" rx="5" fill="#4a6b53" stroke="#241f26" strokeWidth="4" transform="rotate(7)" />
          <g className="jack" stroke="none" fill="#FFD23F">
            <path d="M-22 -44 L-8 -44 L-15 -56 Z" />
            <path d="M22 -44 L8 -44 L15 -56 Z" />
            <path d="M-20 -26 L-12 -20 L-6 -26 L0 -20 L6 -26 L12 -20 L20 -26 L14 -12 L-14 -12 Z" />
          </g>
        </g>
      </g>
  );
}

/** Low fog, drifting, in front of everything including the crowd. */
function HalloweenFog() {
  return (
      <g id="layer-hw-fog" stroke="none">
        <ellipse className="fog" cx="400" cy="1000" rx="340" ry="44" fill="#8E4BF2" opacity="0.08" />
        <ellipse className="fog d3" cx="1000" cy="1030" rx="400" ry="50" fill="#FF7A1A" opacity="0.06" />
        <ellipse className="fog d5" cx="1560" cy="1005" rx="320" ry="42" fill="#8E4BF2" opacity="0.08" />
      </g>
  );
}

/** Garland swagged across the truss, with bulbs that twinkle on the crowd's
 *  own loop. */
function ChristmasAir() {
  return (
      <g id="layer-xm-air">
        <path d="M138 62 Q412 158 686 62 M686 62 Q960 158 1234 62 M1234 62 Q1508 158 1782 62" fill="none" stroke="#2BA85C" strokeWidth="6" />
        <circle className="tw" cx="275" cy="108" r="7" fill="#E84A4A" />
        <circle className="tw t1" cx="412" cy="120" r="7" fill="#F2C94C" />
        <circle className="tw t2" cx="549" cy="108" r="7" fill="#F2F2F2" />
        <circle className="tw t3" cx="823" cy="108" r="7" fill="#F2C94C" />
        <circle className="tw" cx="960" cy="120" r="7" fill="#E84A4A" />
        <circle className="tw t1" cx="1097" cy="108" r="7" fill="#F2F2F2" />
        <circle className="tw t2" cx="1371" cy="108" r="7" fill="#E84A4A" />
        <circle className="tw t3" cx="1508" cy="120" r="7" fill="#F2C94C" />
        <circle className="tw" cx="1645" cy="108" r="7" fill="#F2F2F2" />
      </g>
  );
}

/** A tree beside the booth: trunk, three tiers, a star, and baubles. */
function ChristmasStage() {
  return (
      <g id="layer-xm-stage" transform="translate(470,812)">
        <rect x="-14" y="-36" width="28" height="36" rx="6" fill="#6b4a2f" stroke="#241f26" strokeWidth="4" />
        <path d="M-88 -28 L88 -28 L0 -122 Z" fill="#2BA85C" stroke="#241f26" strokeWidth="5" strokeLinejoin="round" />
        <path d="M-68 -96 L68 -96 L0 -176 Z" fill="#33B85C" stroke="#241f26" strokeWidth="5" strokeLinejoin="round" />
        <path d="M-48 -152 L48 -152 L0 -222 Z" fill="#2BA85C" stroke="#241f26" strokeWidth="5" strokeLinejoin="round" />
        <path d="M0 -252 L4 -238 L18 -238 L7 -229 L11 -215 L0 -224 L-11 -215 L-7 -229 L-18 -238 L-4 -238 Z" fill="#F2C94C" stroke="#241f26" strokeWidth="3" strokeLinejoin="round" />
        <circle className="tw" cx="-40" cy="-52" r="7" fill="#E84A4A" stroke="none" />
        <circle className="tw t1" cx="34" cy="-64" r="7" fill="#F2C94C" stroke="none" />
        <circle className="tw t2" cx="-18" cy="-118" r="7" fill="#F2F2F2" stroke="none" />
        <circle className="tw t3" cx="24" cy="-136" r="7" fill="#E84A4A" stroke="none" />
        <circle className="tw t1" cx="-6" cy="-186" r="6" fill="#F2C94C" stroke="none" />
      </g>
  );
}

/** Snow, falling in front of the room. */
function ChristmasSnow() {
  return (
      <g id="layer-xm-snow" stroke="none" fill="#F2F2F2" opacity="0.9">
        <g transform="translate(140,-20)">
          <g className="fall" style={{ animationDuration: '8.5s' }}>
            <circle r="5" />
          </g>
        </g>
        <g transform="translate(320,-20)">
          <g className="fall" style={{ animationDuration: '7s', animationDelay: '-3s' }}>
            <circle r="4" />
          </g>
        </g>
        <g transform="translate(500,-20)">
          <g className="fall" style={{ animationDuration: '9.5s', animationDelay: '-6s' }}>
            <circle r="6" />
          </g>
        </g>
        <g transform="translate(680,-20)">
          <g className="fall" style={{ animationDuration: '7.5s', animationDelay: '-1.5s' }}>
            <circle r="4.5" />
          </g>
        </g>
        <g transform="translate(860,-20)">
          <g className="fall" style={{ animationDuration: '8s', animationDelay: '-5s' }}>
            <circle r="5" />
          </g>
        </g>
        <g transform="translate(1040,-20)">
          <g className="fall" style={{ animationDuration: '6.8s', animationDelay: '-2.2s' }}>
            <circle r="4" />
          </g>
        </g>
        <g transform="translate(1220,-20)">
          <g className="fall" style={{ animationDuration: '9s', animationDelay: '-7s' }}>
            <circle r="6" />
          </g>
        </g>
        <g transform="translate(1400,-20)">
          <g className="fall" style={{ animationDuration: '7.2s', animationDelay: '-4s' }}>
            <circle r="4.5" />
          </g>
        </g>
        <g transform="translate(1580,-20)">
          <g className="fall" style={{ animationDuration: '8.8s', animationDelay: '-0.8s' }}>
            <circle r="5" />
          </g>
        </g>
        <g transform="translate(1760,-20)">
          <g className="fall" style={{ animationDuration: '7.6s', animationDelay: '-5.6s' }}>
            <circle r="4" />
          </g>
        </g>
      </g>
  );
}

/** Fireworks over the screen, staggered so they never pop together. */
function NewYearAir() {
  return (
      <g id="layer-ny-air">
        <defs>
            <g id="burst" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="currentColor">
              <path d="M0 -20 L0 -70 M0 20 L0 70 M-20 0 L-70 0 M20 0 L70 0 M-14 -14 L-49 -49 M14 -14 L49 -49 M-14 14 L-49 49 M14 14 L49 49" fill="none" />
              <circle cx="0" cy="-80" r="4" stroke="none" />
              <circle cx="0" cy="80" r="4" stroke="none" />
              <circle cx="-80" cy="0" r="4" stroke="none" />
              <circle cx="80" cy="0" r="4" stroke="none" />
              <circle cx="-57" cy="-57" r="4" stroke="none" />
              <circle cx="57" cy="-57" r="4" stroke="none" />
              <circle cx="-57" cy="57" r="4" stroke="none" />
              <circle cx="57" cy="57" r="4" stroke="none" />
            </g>
        </defs>

        <g transform="translate(650,240)">
          <g className="burst d1" style={{ color: '#F2C94C' }}>
            <use href="#burst" />
          </g>
        </g>
        <g transform="translate(1270,200)">
          <g className="burst d3" style={{ color: '#C9CDD6' }}>
            <use href="#burst" />
          </g>
        </g>
        <g transform="translate(960,330) scale(0.75)">
          <g className="burst d5" style={{ color: '#7B84F2' }}>
            <use href="#burst" />
          </g>
        </g>
      </g>
  );
}

/** Confetti, in gold, silver, indigo and white. */
function NewYearConfetti() {
  return (
      <g id="layer-ny-confetti" stroke="none">
        <g transform="translate(180,-20)">
          <g className="fall" style={{ animationDuration: '6.5s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#F2C94C" />
          </g>
        </g>
        <g transform="translate(380,-20)">
          <g className="fall" style={{ animationDuration: '7.5s', animationDelay: '-2s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#C9CDD6" />
          </g>
        </g>
        <g transform="translate(580,-20)">
          <g className="fall" style={{ animationDuration: '6.8s', animationDelay: '-4s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#7B84F2" />
          </g>
        </g>
        <g transform="translate(780,-20)">
          <g className="fall" style={{ animationDuration: '7.8s', animationDelay: '-1s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#F2C94C" />
          </g>
        </g>
        <g transform="translate(980,-20)">
          <g className="fall" style={{ animationDuration: '6.2s', animationDelay: '-5s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#F2F2F2" />
          </g>
        </g>
        <g transform="translate(1180,-20)">
          <g className="fall" style={{ animationDuration: '7.2s', animationDelay: '-3s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#F2C94C" />
          </g>
        </g>
        <g transform="translate(1380,-20)">
          <g className="fall" style={{ animationDuration: '6.6s', animationDelay: '-6s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#C9CDD6" />
          </g>
        </g>
        <g transform="translate(1580,-20)">
          <g className="fall" style={{ animationDuration: '7.6s', animationDelay: '-2.6s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#7B84F2" />
          </g>
        </g>
        <g transform="translate(1780,-20)">
          <g className="fall" style={{ animationDuration: '6.9s', animationDelay: '-4.6s' }}>
            <rect x="-5" y="-3" width="10" height="6" rx="1" fill="#F2C94C" />
          </g>
        </g>
      </g>
  );
}

/** Hearts on top of both speaker stacks, beating. */
function ValentinesStage() {
  return (
      <g id="layer-vd-stage">
        <defs>
            <path
              id="heart"
              d="M0 14 C-16 2 -14 -12 -5 -12 C-1 -12 0 -8 0 -6 C0 -8 1 -12 5 -12 C14 -12 16 2 0 14 Z"
              fill="currentColor"
            />
        </defs>

        <g transform="translate(305,290) scale(3.2)">
          <g className="pulse" style={{ color: '#FF4F79' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(1615,290) scale(3.2)">
          <g className="pulse d2" style={{ color: '#E754A6' }}>
            <use href="#heart" />
          </g>
        </g>
      </g>
  );
}

/** Hearts falling through the room. */
function ValentinesHearts() {
  return (
      <g id="layer-vd-hearts" stroke="none">
        <defs>
            <path
              id="heart"
              d="M0 14 C-16 2 -14 -12 -5 -12 C-1 -12 0 -8 0 -6 C0 -8 1 -12 5 -12 C14 -12 16 2 0 14 Z"
              fill="currentColor"
            />
        </defs>

        <g transform="translate(220,-20)">
          <g className="fall" style={{ animationDuration: '8s', color: '#FF4F79' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(460,-20)">
          <g className="fall" style={{ animationDuration: '7s', animationDelay: '-2.5s', color: '#E754A6' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(700,-20)">
          <g className="fall" style={{ animationDuration: '9s', animationDelay: '-5s', color: '#FF4F79' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(940,-20)">
          <g className="fall" style={{ animationDuration: '7.4s', animationDelay: '-1.4s', color: '#F2C4D0' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(1180,-20)">
          <g className="fall" style={{ animationDuration: '8.4s', animationDelay: '-6s', color: '#E754A6' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(1420,-20)">
          <g className="fall" style={{ animationDuration: '7.8s', animationDelay: '-3.4s', color: '#FF4F79' }}>
            <use href="#heart" />
          </g>
        </g>
        <g transform="translate(1660,-20)">
          <g className="fall" style={{ animationDuration: '8.8s', animationDelay: '-0.9s', color: '#E754A6' }}>
            <use href="#heart" />
          </g>
        </g>
      </g>
  );
}

/** A rainbow rising behind the left speaker stack, behind the truss. */
function PatricksSky() {
  return (
      <g id="layer-sp-sky" fill="none" opacity="0.45">
        <path d="M40 830 A260 260 0 0 1 560 830" stroke="#FF3F4A" strokeWidth="22" />
        <path d="M65 830 A235 235 0 0 1 535 830" stroke="#F2C94C" strokeWidth="22" />
        <path d="M90 830 A210 210 0 0 1 510 830" stroke="#33E58A" strokeWidth="22" />
        <path d="M115 830 A185 185 0 0 1 485 830" stroke="#3F6BD8" strokeWidth="22" />
      </g>
  );
}

/** Two shamrocks flanking the booth. */
function PatricksStage() {
  return (
      <g id="layer-sp-stage">
        <defs>
            <g id="shamrock" fill="currentColor">
              <circle cx="0" cy="-8" r="7" />
              <circle cx="-7" cy="2" r="7" />
              <circle cx="7" cy="2" r="7" />
              <rect x="-1.5" y="4" width="3" height="14" rx="1.5" />
            </g>
        </defs>

        <g transform="translate(622,782) scale(2.4)" style={{ color: '#2BA85C' }}>
          <use href="#shamrock" />
        </g>
        <g transform="translate(1298,786) scale(2.0)" style={{ color: '#33E58A' }}>
          <use href="#shamrock" />
        </g>
      </g>
  );
}

/** Shamrocks falling through the room. */
function PatricksShamrocks() {
  return (
      <g id="layer-sp-shamrocks" stroke="none">
        <defs>
            <g id="shamrock" fill="currentColor">
              <circle cx="0" cy="-8" r="7" />
              <circle cx="-7" cy="2" r="7" />
              <circle cx="7" cy="2" r="7" />
              <rect x="-1.5" y="4" width="3" height="14" rx="1.5" />
            </g>
        </defs>

        <g transform="translate(260,-20)">
          <g className="fall" style={{ animationDuration: '8.2s', color: '#33E58A' }}>
            <use href="#shamrock" />
          </g>
        </g>
        <g transform="translate(540,-20)">
          <g className="fall" style={{ animationDuration: '7.1s', animationDelay: '-3s', color: '#2BA85C' }}>
            <use href="#shamrock" />
          </g>
        </g>
        <g transform="translate(820,-20)">
          <g className="fall" style={{ animationDuration: '9.1s', animationDelay: '-5.5s', color: '#33E58A' }}>
            <use href="#shamrock" />
          </g>
        </g>
        <g transform="translate(1100,-20)">
          <g className="fall" style={{ animationDuration: '7.6s', animationDelay: '-1.8s', color: '#2BA85C' }}>
            <use href="#shamrock" />
          </g>
        </g>
        <g transform="translate(1380,-20)">
          <g className="fall" style={{ animationDuration: '8.6s', animationDelay: '-6.4s', color: '#33E58A' }}>
            <use href="#shamrock" />
          </g>
        </g>
        <g transform="translate(1660,-20)">
          <g className="fall" style={{ animationDuration: '7.3s', animationDelay: '-4.2s', color: '#2BA85C' }}>
            <use href="#shamrock" />
          </g>
        </g>
      </g>
  );
}

/** Bunting along the truss and two fireworks above the screen. */
function IndependenceAir() {
  return (
      <g id="layer-j4-air">
        <defs>
            <g id="burst" stroke="currentColor" strokeWidth="5" strokeLinecap="round" fill="currentColor">
              <path d="M0 -20 L0 -70 M0 20 L0 70 M-20 0 L-70 0 M20 0 L70 0 M-14 -14 L-49 -49 M14 -14 L49 -49 M-14 14 L-49 49 M14 14 L49 49" fill="none" />
              <circle cx="0" cy="-80" r="4" stroke="none" />
              <circle cx="0" cy="80" r="4" stroke="none" />
              <circle cx="-80" cy="0" r="4" stroke="none" />
              <circle cx="80" cy="0" r="4" stroke="none" />
              <circle cx="-57" cy="-57" r="4" stroke="none" />
              <circle cx="57" cy="-57" r="4" stroke="none" />
              <circle cx="-57" cy="57" r="4" stroke="none" />
              <circle cx="57" cy="57" r="4" stroke="none" />
            </g>
        </defs>

        <g stroke="#241f26" strokeWidth="2">
          <path d="M170 64 L206 64 L188 102 Z" fill="#FF3F4A" />
          <path d="M260 64 L296 64 L278 102 Z" fill="#F2F2F2" />
          <path d="M350 64 L386 64 L368 102 Z" fill="#3F6BD8" />
          <path d="M440 64 L476 64 L458 102 Z" fill="#FF3F4A" />
          <path d="M530 64 L566 64 L548 102 Z" fill="#F2F2F2" />
          <path d="M620 64 L656 64 L638 102 Z" fill="#3F6BD8" />
          <path d="M710 64 L746 64 L728 102 Z" fill="#FF3F4A" />
          <path d="M800 64 L836 64 L818 102 Z" fill="#F2F2F2" />
          <path d="M890 64 L926 64 L908 102 Z" fill="#3F6BD8" />
          <path d="M980 64 L1016 64 L998 102 Z" fill="#FF3F4A" />
          <path d="M1070 64 L1106 64 L1088 102 Z" fill="#F2F2F2" />
          <path d="M1160 64 L1196 64 L1178 102 Z" fill="#3F6BD8" />
          <path d="M1250 64 L1286 64 L1268 102 Z" fill="#FF3F4A" />
          <path d="M1340 64 L1376 64 L1358 102 Z" fill="#F2F2F2" />
          <path d="M1430 64 L1466 64 L1448 102 Z" fill="#3F6BD8" />
          <path d="M1520 64 L1556 64 L1538 102 Z" fill="#FF3F4A" />
          <path d="M1610 64 L1646 64 L1628 102 Z" fill="#F2F2F2" />
          <path d="M1700 64 L1736 64 L1718 102 Z" fill="#3F6BD8" />
        </g>
        <g transform="translate(700,240)">
          <g className="burst d2" style={{ color: '#FF3F4A' }}>
            <use href="#burst" />
          </g>
        </g>
        <g transform="translate(1220,280) scale(0.8)">
          <g className="burst d4" style={{ color: '#3F6BD8' }}>
            <use href="#burst" />
          </g>
        </g>
      </g>
  );
}

/**
 * Pumpkins and a gourd by the booth.
 *
 * Uncarved, which is the whole difference from October: the same shapes, no
 * face, no flicker.
 */
function ThanksgivingStage() {
  return (
      <g id="layer-tg-stage">
        <g transform="translate(600,808)">
          <ellipse cx="-24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="0" cy="-34" rx="34" ry="36" fill="#FF7A1A" stroke="#241f26" strokeWidth="5" />
          <rect x="-6" y="-84" width="12" height="20" rx="5" fill="#4a6b53" stroke="#241f26" strokeWidth="4" transform="rotate(-8)" />
        </g>
        <g transform="translate(1310,808) scale(0.75)">
          <ellipse cx="-24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="24" cy="-32" rx="28" ry="32" fill="#E86A10" stroke="#241f26" strokeWidth="5" />
          <ellipse cx="0" cy="-34" rx="34" ry="36" fill="#FF7A1A" stroke="#241f26" strokeWidth="5" />
          <rect x="-6" y="-84" width="12" height="20" rx="5" fill="#4a6b53" stroke="#241f26" strokeWidth="4" transform="rotate(7)" />
        </g>
        <g transform="translate(1382,810)">
          <ellipse cx="0" cy="-26" rx="16" ry="26" fill="#F2C94C" stroke="#241f26" strokeWidth="4" />
          <rect x="-4" y="-62" width="8" height="14" rx="4" fill="#4a6b53" stroke="#241f26" strokeWidth="3" />
        </g>
      </g>
  );
}

/** Autumn leaves, falling. */
function ThanksgivingLeaves() {
  return (
      <g id="layer-tg-leaves" stroke="none">
        <defs>
            <g id="leaf" fill="currentColor">
              <path d="M0 -14 C9 -7 12 2 7 11 C4 16 -4 16 -7 11 C-12 2 -9 -7 0 -14 Z" />
              <rect x="-1.5" y="12" width="3" height="8" rx="1.5" />
            </g>
        </defs>

        <g transform="translate(200,-20)">
          <g className="fall" style={{ animationDuration: '8.6s', color: '#E8963C' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(440,-20)">
          <g className="fall" style={{ animationDuration: '7.3s', animationDelay: '-2.8s', color: '#B8562F' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(680,-20)">
          <g className="fall" style={{ animationDuration: '9.3s', animationDelay: '-5.2s', color: '#C9A227' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(920,-20)">
          <g className="fall" style={{ animationDuration: '7.7s', animationDelay: '-1.6s', color: '#E8963C' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(1160,-20)">
          <g className="fall" style={{ animationDuration: '8.3s', animationDelay: '-6.1s', color: '#B8562F' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(1400,-20)">
          <g className="fall" style={{ animationDuration: '7.9s', animationDelay: '-3.8s', color: '#C9A227' }}>
            <use href="#leaf" />
          </g>
        </g>
        <g transform="translate(1640,-20)">
          <g className="fall" style={{ animationDuration: '8.9s', animationDelay: '-0.7s', color: '#E8963C' }}>
            <use href="#leaf" />
          </g>
        </g>
      </g>
  );
}

/**
 * Every theme's decor, by slot.
 *
 * Keyed by the same strings `config/stage-themes.php` declares, because the
 * server sends one of those keys and this looks it up. A key with nothing here
 * draws nothing, which is exactly what `default` should do - and a theme the
 * catalogue offers with no entry here is still a valid choice that only
 * recolours the fixtures.
 */
const DECOR: Record<string, Partial<Record<DecorSlot, () => ReactNode>>> = {
  halloween: {
    sky: HalloweenSky,
    air: HalloweenAir,
    stage: HalloweenStage,
    front: HalloweenFog,
  },
  thanksgiving: {
    stage: ThanksgivingStage,
    front: ThanksgivingLeaves,
  },
  christmas: {
    air: ChristmasAir,
    stage: ChristmasStage,
    front: ChristmasSnow,
  },
  nye: {
    air: NewYearAir,
    front: NewYearConfetti,
  },
  valentines: {
    stage: ValentinesStage,
    front: ValentinesHearts,
  },
  stpatricks: {
    sky: PatricksSky,
    stage: PatricksStage,
    front: PatricksShamrocks,
  },
  july4: {
    air: IndependenceAir,
  },
};

/**
 * The decor for one slot, if this theme has any.
 *
 * Renders nothing at all rather than an empty group, so an undecorated stage is
 * byte-for-byte the scene it has always been.
 */
export function Decor({ theme, slot }: { theme: string; slot: DecorSlot }) {
  const draw = DECOR[theme]?.[slot];

  return draw === undefined ? null : draw();
}
